import { useState } from "react";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { CalendarIcon, Clock, Mail, X, RefreshCw, Search, Phone, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManageAppointmentPage = () => {
  const [searchType, setSearchType] = useState("email");
  const [searchValue, setSearchValue] = useState("");
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  
  // Reschedule state
  const [rescheduleDialog, setRescheduleDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [newDate, setNewDate] = useState(null);
  const [newTime, setNewTime] = useState("");
  const [timeSlots, setTimeSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  
  // Cancel state
  const [cancelDialog, setCancelDialog] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchValue) return;
    
    setLoading(true);
    try {
      let endpoint = "";
      switch (searchType) {
        case "email":
          endpoint = `${API}/appointments/lookup/${encodeURIComponent(searchValue)}`;
          break;
        case "phone":
          endpoint = `${API}/appointments/lookup-phone/${encodeURIComponent(searchValue)}`;
          break;
        case "name":
          endpoint = `${API}/appointments/lookup-name/${encodeURIComponent(searchValue)}`;
          break;
        default:
          endpoint = `${API}/appointments/lookup/${encodeURIComponent(searchValue)}`;
      }
      
      const response = await axios.get(endpoint);
      setAppointments(response.data);
      setSearched(true);
      
      // Fetch time slots
      const slotsRes = await axios.get(`${API}/time-slots`);
      setTimeSlots(slotsRes.data.slots);
    } catch (error) {
      toast.error("Failed to find appointments");
    } finally {
      setLoading(false);
    }
  };

  const openReschedule = (appointment) => {
    setSelectedAppointment(appointment);
    setNewDate(null);
    setNewTime("");
    setRescheduleDialog(true);
  };

  const openCancel = (appointment) => {
    setSelectedAppointment(appointment);
    setCancelDialog(true);
  };

  const fetchBookedSlots = async (date) => {
    if (date) {
      try {
        const dateStr = format(date, "yyyy-MM-dd");
        const response = await axios.get(`${API}/appointments/booked-slots/${dateStr}`);
        setBookedSlots(response.data.booked_slots);
      } catch (error) {
        console.error("Error fetching booked slots:", error);
      }
    }
  };

  const handleDateSelect = (date) => {
    setNewDate(date);
    setNewTime("");
    fetchBookedSlots(date);
  };

  const handleReschedule = async () => {
    if (!newDate || !newTime) {
      toast.error("Please select a new date and time");
      return;
    }

    setLoading(true);
    try {
      const dateStr = format(newDate, "yyyy-MM-dd");
      await axios.put(
        `${API}/appointments/${selectedAppointment.id}/reschedule?new_date=${dateStr}&new_time=${encodeURIComponent(newTime)}`
      );
      toast.success("Appointment rescheduled successfully");
      setRescheduleDialog(false);
      // Refresh appointments
      handleSearch({ preventDefault: () => {} });
    } catch (error) {
      toast.error("Failed to reschedule appointment");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/appointments/${selectedAppointment.id}/cancel`);
      toast.success("Appointment cancelled successfully");
      setCancelDialog(false);
      // Refresh appointments
      handleSearch({ preventDefault: () => {} });
    } catch (error) {
      toast.error("Failed to cancel appointment");
    } finally {
      setLoading(false);
    }
  };

  const getPlaceholder = () => {
    switch (searchType) {
      case "email":
        return "Enter your email address";
      case "phone":
        return "Enter your phone number";
      case "name":
        return "Enter your first or last name";
      default:
        return "Enter search value";
    }
  };

  const getIcon = () => {
    switch (searchType) {
      case "email":
        return <Mail size={18} strokeWidth={1.5} className="absolute left-0 top-4 text-muted-foreground" />;
      case "phone":
        return <Phone size={18} strokeWidth={1.5} className="absolute left-0 top-4 text-muted-foreground" />;
      case "name":
        return <User size={18} strokeWidth={1.5} className="absolute left-0 top-4 text-muted-foreground" />;
      default:
        return <Search size={18} strokeWidth={1.5} className="absolute left-0 top-4 text-muted-foreground" />;
    }
  };

  return (
    <div data-testid="manage-appointment-page" className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="mono-text text-primary mb-6">Appointment Management</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6">
              Manage Your Appointment
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto font-light">
              Cancel or reschedule your upcoming appointment
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-[600px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Search Type Tabs */}
            <div className="flex justify-center gap-2 mb-8">
              <button
                onClick={() => { setSearchType("email"); setSearchValue(""); setSearched(false); }}
                className={`px-4 py-2 text-xs uppercase tracking-wider border transition-all ${
                  searchType === "email" 
                    ? "border-primary bg-primary/10 text-primary" 
                    : "border-white/10 text-muted-foreground hover:border-white/30"
                }`}
              >
                <Mail size={14} className="inline mr-2" />
                Email
              </button>
              <button
                onClick={() => { setSearchType("phone"); setSearchValue(""); setSearched(false); }}
                className={`px-4 py-2 text-xs uppercase tracking-wider border transition-all ${
                  searchType === "phone" 
                    ? "border-primary bg-primary/10 text-primary" 
                    : "border-white/10 text-muted-foreground hover:border-white/30"
                }`}
              >
                <Phone size={14} className="inline mr-2" />
                Phone
              </button>
              <button
                onClick={() => { setSearchType("name"); setSearchValue(""); setSearched(false); }}
                className={`px-4 py-2 text-xs uppercase tracking-wider border transition-all ${
                  searchType === "name" 
                    ? "border-primary bg-primary/10 text-primary" 
                    : "border-white/10 text-muted-foreground hover:border-white/30"
                }`}
              >
                <User size={14} className="inline mr-2" />
                Name
              </button>
            </div>

            <form onSubmit={handleSearch} className="space-y-6">
              <div className="relative">
                {getIcon()}
                <Input
                  type={searchType === "email" ? "email" : "text"}
                  placeholder={getPlaceholder()}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="luxury-input pl-8"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground px-12 py-6 text-xs uppercase tracking-[0.2em] rounded-none hover:bg-foreground"
              >
                <Search size={16} className="mr-2" />
                {loading ? "Searching..." : "Find My Appointments"}
              </Button>
            </form>

            {/* Results */}
            {searched && (
              <div className="mt-12">
                {appointments.length === 0 ? (
                  <div className="text-center py-12 border border-white/10 bg-card/30">
                    <p className="text-muted-foreground">No upcoming appointments found.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="mono-text text-primary text-xs mb-6">
                      {appointments.length} appointment{appointments.length > 1 ? "s" : ""} found
                    </p>
                    {appointments.map((apt) => (
                      <div
                        key={apt.id}
                        className="border border-white/10 bg-card/30 p-6"
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="space-y-2">
                            <h3 className="font-serif text-lg">{apt.service_name || apt.service_type}</h3>
                            <p className="text-sm text-muted-foreground">{apt.client_name}</p>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CalendarIcon size={14} />
                                {apt.appointment_date && format(parseISO(apt.appointment_date), "MMMM d, yyyy")}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock size={14} />
                                {apt.appointment_time}
                              </span>
                            </div>
                            {apt.estimated_arrival_date && (
                              <p className="text-xs text-primary">
                                Estimated arrival: {format(parseISO(apt.estimated_arrival_date), "MMMM d, yyyy")}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openReschedule(apt)}
                              className="text-xs uppercase tracking-wider border-white/20 hover:bg-primary hover:text-primary-foreground"
                            >
                              <RefreshCw size={14} className="mr-1" />
                              Reschedule
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openCancel(apt)}
                              className="text-xs uppercase tracking-wider border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white"
                            >
                              <X size={14} className="mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialog} onOpenChange={setRescheduleDialog}>
        <DialogContent className="bg-card border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Reschedule Appointment</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Select a new date and time for your appointment.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div>
              <p className="text-sm font-medium mb-3">Select New Date</p>
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={newDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today || date.getDay() === 0;
                  }}
                  className="rounded-none border border-white/10"
                />
              </div>
            </div>
            
            {newDate && (
              <div>
                <p className="text-sm font-medium mb-3">Select New Time</p>
                <div className="grid grid-cols-3 gap-2">
                  {timeSlots.map((slot) => {
                    const isBooked = bookedSlots.includes(slot);
                    return (
                      <button
                        key={slot}
                        onClick={() => !isBooked && setNewTime(slot)}
                        disabled={isBooked}
                        className={`p-3 border text-xs transition-all ${
                          isBooked
                            ? "border-white/5 text-muted-foreground/50 cursor-not-allowed"
                            : newTime === slot
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-white/10 hover:border-primary"
                        }`}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRescheduleDialog(false)}
              className="border-white/20"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReschedule}
              disabled={!newDate || !newTime || loading}
              className="bg-primary text-primary-foreground hover:bg-foreground"
            >
              {loading ? "Rescheduling..." : "Confirm Reschedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Cancel Appointment</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to cancel this appointment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="py-4 border-y border-white/10 my-4">
              <p className="font-medium">{selectedAppointment.service_name || selectedAppointment.service_type}</p>
              <p className="text-sm text-muted-foreground">
                {selectedAppointment.appointment_date && format(parseISO(selectedAppointment.appointment_date), "MMMM d, yyyy")} at {selectedAppointment.appointment_time}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialog(false)}
              className="border-white/20"
            >
              Keep Appointment
            </Button>
            <Button
              onClick={handleCancel}
              disabled={loading}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {loading ? "Cancelling..." : "Yes, Cancel Appointment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageAppointmentPage;
